import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'src/components';
import { UserDictionaryApi } from 'src/api';
import { LearnablePronunciationData } from 'src/api/userDictionary/types';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { useBuddySessionId } from 'src/hooks/useBuddySessionId';
import theme from 'src/utils/theme';
import { LearnablePronunciation } from '../LearnablePronunciation';
import { getPronunciationModeFromContext } from '../utils/getPronunciationModeFromContext';
import * as S from './styles';

const msgs = defineMessages({
  finishCta: { id: 'pronunciationBuddy_endOfSession_finishCTA' },
  restartCta: { id: 'pronunciationBuddy_endOfSession_goAgainCTA' },
});

export const PronunciationModal: FC<{
  learnableId: number;
  onActivityEngagement: () => void;
  onClose: () => void;
}> = ({ learnableId, onActivityEngagement, onClose }) => {
  const intl = useIntl();
  const languagePairId = getLanguagePairId();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  const { sessionId, generateNewSession } = useBuddySessionId();

  const mode = getPronunciationModeFromContext();

  const { data: pronunciationData } = useQuery<LearnablePronunciationData[] | null>({
    queryKey: ['pronunciation_data', languagePairId, [learnableId], mode, sessionId],
    queryFn: () =>
      UserDictionaryApi.getLearnablesPronunciationData(
        languagePairId as number,
        [learnableId],
        mode,
        sessionId,
      ),
    enabled: !!languagePairId,
  });
  const learnable =
    pronunciationData && pronunciationData.find(d => d.learnableIdKey === learnableId);

  return (
    <S.Modal isOpen handleClose={onClose} darkCloseIcon fullSize={isMobile}>
      <S.ModalContent>
        {learnable && (
          <LearnablePronunciation
            key={sessionId}
            learnable={learnable}
            continueButtons={
              <S.ButtonsWrapper>
                <Button buttonType="primary" buttonSize="large" wide onClick={onClose}>
                  {intl.formatMessage(msgs.finishCta)}
                </Button>
                <Button
                  buttonType="secondary"
                  buttonSize="large"
                  wide
                  onClick={() => {
                    generateNewSession();
                  }}
                >
                  {intl.formatMessage(msgs.restartCta)}
                </Button>
              </S.ButtonsWrapper>
            }
            onActivityEngagement={onActivityEngagement}
            onContinue={onClose}
            sessionId={sessionId}
          />
        )}
      </S.ModalContent>
    </S.Modal>
  );
};
